// ═══════════════════════════════════════════════════════════════════════════════
// Employer dashboard (/dashboard) — UI aligned with UI/employer/dashboard.html
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  APPROVAL_STATUS,
  SETTLEMENT_STATUS,
  USER_ROLE,
  type BudgetListItem,
  type EmployerGettingStartedProgress,
  type EmployerPendingReviewItem,
  type EmployerTodayTaskItem,
  type PinMessage,
  type RestorableArchivedTaskItem,
  type SystemMessage,
} from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { budgetApi, membershipApi, pairingImportApi, pinMessageApi, settingsApi, settlementApi, systemMessageApi, taskInstanceApi } from '../../lib/api';
import { RestoreArchivedTasksModal } from '../../components/membership/RestoreArchivedTasksModal';
import { ImportPreviousPairingModal } from '../../components/pairing/ImportPreviousPairingModal';
import { PinMessageTicker } from '../../components/pinMessage/PinMessageTicker';
import { SystemMessageTicker } from '../../components/systemMessage/SystemMessageTicker';
import { useToast } from '../../components/ui/ToastProvider';
import { resolveDisplayTimeZone } from '../../lib/userTodayYmd';
import { PagePrimaryFab } from '../../components/ui/PagePrimaryFab';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { usePinMessageCreationGate } from '../../hooks/usePinMessageCreationGate';
import { WeatherGreetingHeadingEmoji } from '../../components/home/WeatherGreetingHeadingEmoji';
import { WeatherGreetingSubheading } from '../../components/home/WeatherGreetingSubheading';
import { EmployerTodayCompletionArcGauge } from '../../components/home/EmployerTodayCompletionArcGauge';
import { PendingReviewClipboardWatermark } from '../../components/home/PendingReviewClipboardWatermark';
import { EmployerPendingReviewDashboardCard } from '../../components/home/EmployerPendingReviewDashboardCard';
import {
  EmployerGettingStartedCard,
  type GettingStartedStepId,
} from '../../components/home/EmployerGettingStartedCard';
import { EmployerGettingStartedHelpPopover } from '../../components/home/EmployerGettingStartedHelpPopover';
import { sortEmployerTasksByDueDateNewestFirst } from '../../utils/employerTaskDayGrouping';
import { OverdueTasksSummaryCard } from '../../components/home/OverdueTasksSummaryCard';
import { EmployerMoneyGivenDashboardCard } from '../../components/home/EmployerMoneyGivenDashboardCard';
import {
  getCurrentHourInTimezone,
  resolveGreetingI18nKey,
  resolveGreetingTimeBucket,
} from '../../lib/greetingTimeOfDay';

const pickPreferredBudget = (rows: BudgetListItem[], todayYmd: string): BudgetListItem | undefined => {
  const currentPeriodBudget = rows.find((row) => row.startDate <= todayYmd && row.endDate >= todayYmd);
  if (currentPeriodBudget) {
    return currentPeriodBudget;
  }
  const targetMs = new Date(`${todayYmd}T00:00:00`).getTime();
  return [...rows].sort((a, b) => {
    const aDiff = Math.abs(new Date(`${a.startDate}T00:00:00`).getTime() - targetMs);
    const bDiff = Math.abs(new Date(`${b.startDate}T00:00:00`).getTime() - targetMs);
    if (aDiff !== bDiff) {
      return aDiff - bDiff;
    }
    return a.startDate.localeCompare(b.startDate);
  })[0];
};

const getTodayYmdByTimezone = (timezone: string): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value ?? '1970';
  const month = parts.find((part) => part.type === 'month')?.value ?? '01';
  const day = parts.find((part) => part.type === 'day')?.value ?? '01';
  return `${year}-${month}-${day}`;
};

export const EmployerDashboardPage = () => {
  const { t, language } = useLanguage();
  const { showErrorToast, showSuccessToast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { guardNavigateToCreatePinMessage } = usePinMessageCreationGate();

  const [todayTasks, setTodayTasks] = useState<EmployerTodayTaskItem[]>([]);
  const [pendingItems, setPendingItems] = useState<EmployerPendingReviewItem[]>([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [budgetRows, setBudgetRows] = useState<BudgetListItem[]>([]);
  const [submittedSettlementCount, setSubmittedSettlementCount] = useState(0);
  const [firstSubmittedSettlementId, setFirstSubmittedSettlementId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pinMessages, setPinMessages] = useState<PinMessage[]>([]);
  const [pinMessageRotationSeconds, setPinMessageRotationSeconds] = useState(5);
  const [systemMessages, setSystemMessages] = useState<SystemMessage[]>([]);
  const [systemMessageRotationSeconds, setSystemMessageRotationSeconds] = useState(5);
  const [deleteTargetPinMessage, setDeleteTargetPinMessage] = useState<PinMessage | null>(null);
  const [overdueTaskCount, setOverdueTaskCount] = useState<number | null>(null);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [restoreModalItems, setRestoreModalItems] = useState<RestorableArchivedTaskItem[]>([]);
  const [restoreModalBusy, setRestoreModalBusy] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importModalBusy, setImportModalBusy] = useState(false);
  const [gettingStarted, setGettingStarted] = useState<EmployerGettingStartedProgress | null>(null);
  const [gettingStartedBusy, setGettingStartedBusy] = useState(false);

  const timezone = resolveDisplayTimeZone(user?.settings.timezone);
  const todayYmd = useMemo(() => getTodayYmdByTimezone(timezone), [timezone]);

  const greetingHour = useMemo(() => getCurrentHourInTimezone(timezone), [timezone]);

  const greetingBucket = useMemo(() => resolveGreetingTimeBucket(greetingHour), [greetingHour]);

  const greeting = useMemo(
    () => t(resolveGreetingI18nKey(greetingBucket, 'employer'), { name: user?.nickname ?? '' }),
    [greetingBucket, t, user?.nickname],
  );


  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [todayRes, pendingRes, budgetRes, settlementRes, pinMessageRes, overdueRes, systemMessageRes, gettingStartedRes] =
        await Promise.all([
        taskInstanceApi.employerTodayList(),
        taskInstanceApi.pendingReview(),
        budgetApi.list('active', 1, 100),
        settlementApi.list('all', 1, 100),
        pinMessageApi.list(false),
        taskInstanceApi.overdueList(),
        systemMessageApi.list(language, true),
        settingsApi.getGettingStarted(),
      ]);

      if (!todayRes.success || !todayRes.data) {
        setError(todayRes.error ?? t('employerDashboard.loadError'));
        return;
      }
      if (!pendingRes.success || !pendingRes.data) {
        setError(pendingRes.error ?? t('employerDashboard.loadError'));
        return;
      }

      setTodayTasks(todayRes.data.tasks ?? []);
      setPendingItems(sortEmployerTasksByDueDateNewestFirst(pendingRes.data.tasks ?? []).slice(0, 3));
      setPendingTotal(pendingRes.data.total ?? pendingRes.data.tasks.length);

      if (budgetRes.success && budgetRes.data) {
        const raw = Array.isArray(budgetRes.data) ? budgetRes.data : (budgetRes.data.items ?? []);
        setBudgetRows(raw.filter((row) => row.isFromPreviousHelper !== true));
      } else {
        setBudgetRows([]);
      }

      if (settlementRes.success && settlementRes.data) {
        const list = Array.isArray(settlementRes.data)
          ? settlementRes.data
          : (settlementRes.data.items ?? []);
        const submittedSettlements = list.filter(
          (row) =>
            row.status === SETTLEMENT_STATUS.SUBMITTED && row.isFromPreviousHelper !== true,
        );
        setSubmittedSettlementCount(submittedSettlements.length);
        setFirstSubmittedSettlementId(submittedSettlements.length === 1 ? submittedSettlements[0].id : null);
      } else {
        setSubmittedSettlementCount(0);
        setFirstSubmittedSettlementId(null);
      }

      if (pinMessageRes.success && pinMessageRes.data) {
        setPinMessages(pinMessageRes.data.items ?? []);
        setPinMessageRotationSeconds(pinMessageRes.data.rotationSeconds ?? 5);
      } else {
        setPinMessages([]);
        setPinMessageRotationSeconds(5);
      }

      if (systemMessageRes.success && systemMessageRes.data) {
        setSystemMessages(systemMessageRes.data.items ?? []);
        setSystemMessageRotationSeconds(systemMessageRes.data.rotationSeconds ?? 5);
      } else {
        setSystemMessages([]);
        setSystemMessageRotationSeconds(5);
      }

      if (overdueRes.success && overdueRes.data) {
        setOverdueTaskCount(overdueRes.data.tasks?.length ?? 0);
      } else {
        setOverdueTaskCount(null);
      }

      if (gettingStartedRes.success && gettingStartedRes.data) {
        setGettingStarted(gettingStartedRes.data);
      } else {
        setGettingStarted(null);
      }
    } catch {
      setError(t('employerDashboard.loadError'));
    } finally {
      setLoading(false);
    }
  }, [language, t, todayYmd]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const loadRestoreModal = async (): Promise<void> => {
      try {
        const res = await membershipApi.getRestoreModal();
        if (res.success && res.data?.shouldShow && (res.data.items?.length ?? 0) > 0) {
          setRestoreModalItems(res.data.items);
          setRestoreModalOpen(true);
        }
      } catch {
        // Non-blocking dashboard enhancement
      }
    };
    if (user?.role === USER_ROLE.EMPLOYER) {
      void loadRestoreModal();
    }
  }, [user?.role]);

  useEffect(() => {
    const loadImportModal = async (): Promise<void> => {
      try {
        const res = await pairingImportApi.getModal();
        if (res.success && res.data?.shouldShow) {
          setImportModalOpen(true);
        }
      } catch {
        // Non-blocking dashboard enhancement
      }
    };
    if (user?.role === USER_ROLE.EMPLOYER) {
      void loadImportModal();
    }
  }, [user?.role]);

  const handleImportModalLater = async (): Promise<void> => {
    setImportModalBusy(true);
    try {
      await pairingImportApi.dismissModal();
      setImportModalOpen(false);
    } catch {
      showErrorToast(t('pairing.import.error'));
    } finally {
      setImportModalBusy(false);
    }
  };

  const handleImportModalImported = async (): Promise<void> => {
    setImportModalBusy(true);
    try {
      await pairingImportApi.dismissModal();
      setImportModalOpen(false);
    } catch {
      showErrorToast(t('pairing.import.error'));
    } finally {
      setImportModalBusy(false);
    }
  };

  const handleRestoreModalSkip = async (): Promise<void> => {
    setRestoreModalBusy(true);
    try {
      await membershipApi.dismissRestoreModal();
      setRestoreModalOpen(false);
    } catch {
      showErrorToast(t('membership.restoreModal.error'));
    } finally {
      setRestoreModalBusy(false);
    }
  };

  const handleRestoreModalConfirm = async (taskIds: string[]): Promise<void> => {
    setRestoreModalBusy(true);
    try {
      const res = await membershipApi.restoreArchivedTasks(taskIds);
      if (!res.success) {
        showErrorToast(res.error ?? t('membership.restoreModal.error'));
        return;
      }
      await membershipApi.dismissRestoreModal();
      setRestoreModalOpen(false);
      showSuccessToast(t('membership.restoreModal.success'));
    } catch {
      showErrorToast(t('membership.restoreModal.error'));
    } finally {
      setRestoreModalBusy(false);
    }
  };

  const primaryBudget = useMemo(
    () => pickPreferredBudget(budgetRows, todayYmd),
    [budgetRows, todayYmd],
  );
  const primaryBudgetRange = primaryBudget
    ? t('settlement.period', {
        start: primaryBudget.startDate.slice(5, 10),
        end: primaryBudget.endDate.slice(5, 10),
      })
    : '';

  if (!user) {
    return null;
  }
  if (user.role !== USER_ROLE.EMPLOYER) {
    return <Navigate to="/home" replace />;
  }

  const totalToday = todayTasks.length;
  const completedToday = todayTasks.filter((x) => x.status === APPROVAL_STATUS.APPROVED).length;
  const pct =
    totalToday === 0 ? 0 : Math.round((completedToday / Math.max(totalToday, 1)) * 100);

  const handlePendingRow = (id: string) => {
    navigate(`/task-instances/${id}/review`);
  };

  const handleViewAllPending = useCallback(() => {
    navigate('/tasks/pending-review');
  }, [navigate]);

  const handleSettlementAlert = () => {
    if (submittedSettlementCount === 1 && firstSubmittedSettlementId) {
      navigate(`/settlements/${firstSubmittedSettlementId}`);
      return;
    }
    if (submittedSettlementCount > 1) {
      navigate('/settlements/pending-review');
      return;
    }
    navigate('/settlements');
  };

  const handleTodayCompletionClick = () => {
    navigate('/tasks');
  };

  const handleTodayCompletionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleTodayCompletionClick();
    }
  };

  const handleOverdueTasksClick = () => {
    navigate('/tasks/overdue');
  };

  const handleGettingStartedDismiss = useCallback(async (): Promise<void> => {
    setGettingStartedBusy(true);
    try {
      const res = await settingsApi.dismissGettingStarted();
      if (res.success && res.data) {
        setGettingStarted(res.data);
        showSuccessToast(t('employerDashboard.gettingStarted.dismissToast'));
        return;
      }
      setGettingStarted((prev) => (prev ? { ...prev, shouldShow: false, dismissed: true } : null));
      showSuccessToast(t('employerDashboard.gettingStarted.dismissToast'));
    } catch {
      showErrorToast(t('employerDashboard.gettingStarted.dismissError'));
    } finally {
      setGettingStartedBusy(false);
    }
  }, [showErrorToast, showSuccessToast, t]);

  const handleGettingStartedStepClick = useCallback(
    (stepId: GettingStartedStepId): void => {
      if (stepId === 'task') {
        navigate('/tasks');
        return;
      }
      if (stepId === 'budget') {
        navigate('/budgets/new');
        return;
      }
      if (stepId === 'family') {
        navigate('/family-members');
        return;
      }
      void guardNavigateToCreatePinMessage(navigate, '/pin-messages/new');
    },
    [guardNavigateToCreatePinMessage, navigate],
  );

  const handleEditPinMessage = (item: PinMessage) => {
    navigate(`/pin-messages/${item.id}/edit`);
  };

  const handleRemovePinMessage = (item: PinMessage) => {
    setDeleteTargetPinMessage(item);
  };

  const handleConfirmDeletePinMessage = async () => {
    if (!deleteTargetPinMessage) {
      return;
    }
    const targetId = deleteTargetPinMessage.id;
    setDeleteTargetPinMessage(null);
    try {
      await pinMessageApi.remove(targetId);
      setPinMessages((prev) => prev.filter((row) => row.id !== targetId));
      showSuccessToast(t('toast.pinMessage.deleteSuccess'));
    } catch {
      const message = t('pinMessage.error.remove');
      setError(message);
      showErrorToast(message);
    }
  };

  return (
    <main className="px-4 py-6">
      <div className="mb-5">
        <SystemMessageTicker
          items={systemMessages}
          rotationSeconds={systemMessageRotationSeconds}
          onOpenDetail={(item) => {
            if (!item.isRead) {
              void systemMessageApi.markRead(item.id).then(() => {
                setSystemMessages((prev) =>
                  prev.map((row) => (row.id === item.id ? { ...row, isRead: true } : row)),
                );
              });
            }
          }}
        />
        <div className="flex items-center gap-2.5">
          <WeatherGreetingHeadingEmoji hour={greetingHour} />
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">{greeting}</h1>
          {!loading && !error && gettingStarted?.showHelpIcon ? (
            <EmployerGettingStartedHelpPopover
              progress={gettingStarted}
              onStepClick={handleGettingStartedStepClick}
            />
          ) : null}
        </div>
        <WeatherGreetingSubheading />
        <PinMessageTicker
          items={pinMessages}
          rotationSeconds={pinMessageRotationSeconds}
          canManage
          onEditCurrent={handleEditPinMessage}
          onRemoveCurrent={handleRemovePinMessage}
        />
      </div>

      {!loading && !error && gettingStarted?.shouldShow ? (
        <div className="mb-5">
          <EmployerGettingStartedCard
            progress={gettingStarted}
            busy={gettingStartedBusy}
            onDismiss={() => {
              void handleGettingStartedDismiss();
            }}
            onStepClick={handleGettingStartedStepClick}
          />
        </div>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-16" role="status" aria-label={t('common.loading')}>
          <div className="h-9 w-9 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
        </div>
      ) : null}

      {!loading && error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center text-sm text-red-700" role="alert">
          {error}
          <button
            type="button"
            onClick={() => void load()}
            className="mt-2 block w-full text-sm font-semibold text-primary-700 underline"
          >
            {t('task.action.refresh')}
          </button>
        </div>
      ) : null}

      {!loading && !error ? (
        <div className="flex flex-col gap-5 pb-14">
          <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-2">
            <section
              role="button"
              tabIndex={0}
              aria-label={t('employerDashboard.ariaTodayCompletionCard')}
              onClick={handleTodayCompletionClick}
              onKeyDown={handleTodayCompletionKeyDown}
              className="relative flex min-h-full min-w-0 cursor-pointer flex-col items-center overflow-hidden rounded-3xl border border-primary-200 bg-white p-4 shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 min-[400px]:p-3 sm:p-4 dark:border-gray-700 dark:bg-gray-900/80"
              aria-labelledby="dash-completion-heading"
            >
              <div
                className="pointer-events-none absolute -right-8 -top-6 opacity-[0.6] dark:opacity-[0.45]"
                aria-hidden
              >
                <PendingReviewClipboardWatermark className="h-[200px] w-[340px] max-w-none" />
              </div>
              <div className="relative z-10 flex w-full flex-col items-center">
                <h2
                  id="dash-completion-heading"
                  className="mb-1 w-full shrink-0 text-center text-sm font-semibold text-gray-900 min-[400px]:text-sm sm:text-sm dark:text-gray-100"
                >
                  {t('employerDashboard.todayCompletion')}
                </h2>
                <EmployerTodayCompletionArcGauge
                  percent={pct}
                  dateYmd={todayYmd}
                  completionFractionLabel={t('employerDashboard.completedFraction', {
                    done: completedToday,
                    total: totalToday,
                  })}
                  completionFractionValue={t('employerDashboard.completedFractionValue', {
                    done: completedToday,
                    total: totalToday,
                  })}
                  completionFractionStatus={t('employerDashboard.completedFractionStatus')}
                />
              </div>
            </section>
            <OverdueTasksSummaryCard
              count={overdueTaskCount}
              title={t('helperHome.cardOverdueTasks')}
              hint={t('helperHome.overdueTasksHint')}
              placeholderLabel={t('helperHome.dataPlaceholder')}
              ariaLabel={t('employerDashboard.ariaOverdueCard')}
              onClick={handleOverdueTasksClick}
            />
          </div>

          <EmployerPendingReviewDashboardCard
            items={pendingItems}
            pendingTotal={pendingTotal}
            onRowClick={handlePendingRow}
            onViewAll={handleViewAllPending}
          />

          <EmployerMoneyGivenDashboardCard
            primaryBudget={primaryBudget}
            primaryBudgetRange={primaryBudgetRange}
            submittedSettlementCount={submittedSettlementCount}
            onSettlementAlert={handleSettlementAlert}
          />
        </div>
      ) : null}

      <PagePrimaryFab
        onClick={() => {
          void guardNavigateToCreatePinMessage(navigate, '/pin-messages/new');
        }}
        ariaLabel={t('pinMessage.action.add')}
      >
        <span className="text-lg font-bold leading-none" aria-hidden>+</span>
        <span>{t('pinMessage.action.add')}</span>
      </PagePrimaryFab>

      <ConfirmDialog
        open={Boolean(deleteTargetPinMessage)}
        title={t('pinMessage.action.remove')}
        description={t('pinMessage.confirm.remove')}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => { void handleConfirmDeletePinMessage(); }}
        onCancel={() => setDeleteTargetPinMessage(null)}
        variant="danger"
      />

      <RestoreArchivedTasksModal
        open={restoreModalOpen}
        items={restoreModalItems}
        busy={restoreModalBusy}
        onConfirm={(taskIds) => {
          void handleRestoreModalConfirm(taskIds);
        }}
        onSkip={() => {
          void handleRestoreModalSkip();
        }}
      />

      <ImportPreviousPairingModal
        open={importModalOpen}
        busy={importModalBusy}
        onLater={() => {
          void handleImportModalLater();
        }}
        onImported={() => {
          void handleImportModalImported();
        }}
      />
    </main>
  );
};
